import '@/App.css';
import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'sonner';
import Navbar from './components/Navbar';
import OldDomainBanner from './components/OldDomainBanner';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import DashboardPage from './pages/DashboardPage';
import ChallengesPage from './pages/ChallengesPage';
import ActivityPage from './pages/ActivityPage';
import TeamPage from './pages/TeamPage';
import FundraisingPage from './pages/FundraisingPage';
import LeaderboardPage from './pages/LeaderboardPage';
import AdminPage from './pages/AdminPage';
import ProfilePage from './pages/ProfilePage';
import SupportersPage from './pages/SupportersPage';
import SupporterDashboard from './pages/SupporterDashboard';
import OnboardingPage from './pages/OnboardingPage';
import TeammateSignupPage from './pages/TeammatSignupPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PrivacyPage from './pages/PrivacyPage';
import FaqPage from './pages/FaqPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import AndroidDownloadPage from './pages/AndroidDownloadPage';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user || user.role !== 'admin') return <Navigate to="/" />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/challenges" element={<PrivateRoute><ChallengesPage /></PrivateRoute>} />
      <Route path="/onboarding" element={<PrivateRoute><OnboardingPage /></PrivateRoute>} />
      <Route path="/activity" element={<PrivateRoute><ActivityPage /></PrivateRoute>} />
      <Route path="/team" element={<PrivateRoute><TeamPage /></PrivateRoute>} />
      <Route path="/supporters" element={<PrivateRoute><SupportersPage /></PrivateRoute>} />
      <Route path="/supporter-dashboard" element={<PrivateRoute><SupporterDashboard /></PrivateRoute>} />
      <Route path="/fundraise/:userId" element={<FundraisingPage />} />
      <Route path="/f/:userId" element={<FundraisingPage />} />
      <Route path="/leaderboard" element={<LeaderboardPage />} />
      <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
      <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
      <Route path="/teams/join/:inviteCode" element={<TeammateSignupPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/account/delete" element={<DeleteAccountPage />} />
      <Route path="/android" element={<AndroidDownloadPage />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  // Kill the deploy-pipeline-injected "Made with Emergent" badge.
  // Emergent's CDN injects an <a id="emergent-badge"> into deployed HTML
  // that our public/index.html edits cannot remove. We strip it at runtime
  // and watch for re-injection.
  useEffect(() => {
    function nuke() {
      const el = document.getElementById('emergent-badge');
      if (el && el.parentNode) {
        try { el.parentNode.removeChild(el); } catch (e) { /* noop */ }
      }
    }
    // Inject a CSS rule as a first line of defence (prevents any flash)
    if (!document.getElementById('emergent-badge-killer-style')) {
      const s = document.createElement('style');
      s.id = 'emergent-badge-killer-style';
      s.textContent = '#emergent-badge{display:none!important;visibility:hidden!important;opacity:0!important;pointer-events:none!important}';
      document.head.appendChild(s);
    }
    nuke();
    // Re-poll briefly to catch late injection
    let tries = 0;
    const iv = setInterval(() => {
      nuke();
      if (++tries > 100) clearInterval(iv); // ~10 s
    }, 100);
    // Long-term watch via MutationObserver
    const mo = new MutationObserver(nuke);
    if (document.documentElement) {
      mo.observe(document.documentElement, { childList: true, subtree: true });
    }
    return () => { clearInterval(iv); mo.disconnect(); };
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="min-h-screen bg-stone-50">
          <OldDomainBanner />
          <Navbar />
          <AppRoutes />
        </div>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
